import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { ChevronRight, ExpandMore, ExpandLess } from "@mui/icons-material";
import type { StatusOptionConfig } from "../types/status.types";
import StatusModal from "./StatusModal";
import { PORTAL_HEADER_STATUS_DOT_CLASS } from "@constants/portalUiTheme";
import { getOutcomeStatusLabel } from "../utils/statusI18n";

interface StatusDropdownProps {
	selectedStatus: string;
	isDropdownOpen: boolean;
	dropdownRef: React.RefObject<HTMLDivElement | null>;
	onStatusSelect: (status: string, requiresModal: boolean, statusId?: string, apiCode?: string) => void;
	onToggleDropdown: () => void;
	isModalOpen: boolean;
	pendingStatus: string | null;
	onCloseModal: () => void;
	onStatusUpdated?: (statusName: string) => void;
	statusOptions: StatusOptionConfig[];
	loadingStatuses: boolean;
	isProfileComplete?: boolean;
}

const StatusDropdown: React.FC<StatusDropdownProps> = ({
	selectedStatus,
	isDropdownOpen,
	dropdownRef,
	onStatusSelect,
	onToggleDropdown,
	isModalOpen,
	pendingStatus,
	onCloseModal,
	onStatusUpdated,
	statusOptions,
	loadingStatuses,
	isProfileComplete = true,
}) => {
	const { t } = useTranslation();

	const isAlwaysVisibleOption = (option: StatusOptionConfig) => {
		const code = option.apiCode;
		return code === "EMPLOYED" || code === "SELF_EMPLOYED" || Boolean(code?.startsWith("HIGHER"));
	};

	const availableOptions = statusOptions.filter(
		option => option.value !== selectedStatus || isAlwaysVisibleOption(option),
	);

	const selectedOption = useMemo(
		() => statusOptions.find(option => option.value === selectedStatus),
		[statusOptions, selectedStatus],
	);

	const selectedStatusLabel = getOutcomeStatusLabel(t, selectedOption?.apiCode, selectedStatus);

	const arrowIcon = isDropdownOpen ? (
		<ExpandLess className="text-gray-400" />
	) : (
		<ExpandMore className="text-gray-400" />
	);

	return (
		<>
			<div className="relative" ref={dropdownRef}>
				<button
					type="button"
					onClick={onToggleDropdown}
					className="header-status-pill"
					aria-label={t("feature.status.dropdownAriaLabel")}
					aria-expanded={isDropdownOpen}>
					<div className={PORTAL_HEADER_STATUS_DOT_CLASS} aria-hidden />
					<span className="whitespace-nowrap">
						{t("feature.status.statusWithValue", { status: selectedStatusLabel })}
					</span>
					<span className="flex shrink-0 items-center">{arrowIcon}</span>
				</button>

				{isDropdownOpen && isProfileComplete && (
					<div className="absolute right-0 z-50 mt-2 w-56 overflow-hidden rounded-lg border border-gray-200 bg-white shadow-lg">
						<div className="p-4">
							{(() => {
								if (loadingStatuses) {
									return (
										<div className="px-4 py-2 text-sm text-gray-500">{t("feature.status.loading")}</div>
									);
								}
								if (availableOptions.length === 0) {
									return (
										<div className="px-4 py-2 text-sm text-gray-500">{t("feature.status.noOptions")}</div>
									);
								}
								return availableOptions.map(option => {
									const requiresModal = option.requiresModal;
									const optionLabel = getOutcomeStatusLabel(t, option.apiCode, option.label);

									return (
										<button
											key={option.value}
											type="button"
											onClick={() =>
												onStatusSelect(option.value, option.requiresModal, option.statusId, option.apiCode)
											}
											className={`flex w-full items-center justify-between rounded-lg bg-white px-3 py-2.5 text-left text-gray-800 transition-colors duration-150 hover:bg-accent-40 hover:text-accent ${
												option.value === selectedStatus ? "bg-accent-40 font-semibold text-accent" : ""
											}`}>
											<div className="flex flex-1 items-center gap-2">
												<span className="font-medium text-gray-900">{optionLabel}</span>
											</div>
											<div className="flex items-center gap-2">
												{requiresModal && <ChevronRight className="h-4 w-4 text-gray-400" />}
											</div>
										</button>
									);
								});
							})()}
						</div>
					</div>
				)}
			</div>

			{pendingStatus &&
				(() => {
					const pendingOption = statusOptions.find(opt => opt.value === pendingStatus);
					const modalType = pendingOption?.modalType;

					if (!modalType) return null;

					if (modalType !== "Employed" && modalType !== "Self Employed" && modalType !== "Higher Studies") {
						return null;
					}

					return (
						<StatusModal
							isOpen={isModalOpen}
							onClose={onCloseModal}
							statusType={modalType}
							statusId={pendingOption?.statusId}
							statusApiCode={pendingOption?.apiCode}
							onStatusUpdated={onStatusUpdated}
						/>
					);
				})()}
		</>
	);
};

export default StatusDropdown;
